import { useState, type FormEvent } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { api } from "../../lib/api.js";
import { AuthCardShell } from "../../components/auth-card-shell.js";

export const Route = createFileRoute("/_public/signup")({
  component: SignupPage,
});

const API_URL = import.meta.env.VITE_API_URL ?? "";

function SignupPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api.post("/auth/signup", {
        email,
        password,
        displayName: displayName.trim() || undefined,
      });
      setSubmitted(true);
    } catch (err: unknown) {
      type ApiErr = { response?: { data?: { errors?: Array<{ message: string }>; message?: string } } };
      const data = (err as ApiErr).response?.data;
      const msg = data?.errors?.[0]?.message ?? data?.message ?? "Signup failed";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <AuthCardShell>
        <div className="brut-card">
          <h1
            style={{
              fontSize: 32,
              fontWeight: 900,
              letterSpacing: "-0.03em",
              margin: "0 0 12px",
            }}
          >
            Check your email
          </h1>
          <p>
            We sent a verification link to <strong>{email}</strong>. Click it
            to finish setting up your account.
          </p>
          <p style={{ fontSize: 14, color: "var(--muted)", marginTop: 24 }}>
            Didn't receive it? Check spam, or{" "}
            <Link to="/login">return to sign in</Link>.
          </p>
        </div>
      </AuthCardShell>
    );
  }

  const pwStrength = passwordStrength(password);

  return (
    <div className="auth-shell">
      <header className="auth-header">
        <div className="auth-header-inner">
          <a href="/" className="auth-logo">
            <span className="auth-logo-mark">O!</span>
            <span className="auth-logo-text">oyereply</span>
          </a>
          <a href="/" className="auth-header-link">
            ← Back to home
          </a>
        </div>
      </header>

      <main className="auth-grid signup">
        <section
          className="auth-promo bg-dot"
          style={{ background: "var(--accent)" }}
        >
          <div>
            <span className="stamp" style={{ background: "var(--paper)" }}>
              Create account · free forever
            </span>
            <h1 className="auth-display-h1" style={{ marginTop: 20 }}>
              Let's get
              <br />
              your DMs in <span className="text-stroke">order.</span>
            </h1>
            <p
              style={{
                marginTop: 20,
                fontSize: 16,
                maxWidth: 360,
                color: "rgba(14,14,14,0.82)",
              }}
            >
              No credit card. No 14-day countdown. Cancel anytime, in two
              clicks.
            </p>

            <ul className="checklist">
              <li>
                <span className="checklist-step">1</span>
                <span>
                  <b>Create your workspace.</b> Just an email. Takes 20
                  seconds.
                </span>
              </li>
              <li>
                <span className="checklist-step">2</span>
                <span>
                  <b>Connect Instagram.</b> Read-only by default. Revoke any
                  time.
                </span>
              </li>
              <li>
                <span className="checklist-step">3</span>
                <span>
                  <b>Pick a starter flow.</b> Comment-to-DM, story reply, or
                  build your own.
                </span>
              </li>
            </ul>
          </div>

          <div className="proof-card">
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div className="proof-avatars">
                <div style={{ background: "var(--peach)" }} />
                <div style={{ background: "var(--sage)" }} />
                <div style={{ background: "var(--lilac)" }} />
                <div
                  style={{
                    background: "var(--accent)",
                    display: "grid",
                    placeItems: "center",
                    fontSize: 10,
                    fontFamily: "var(--font-display)",
                    fontWeight: 800,
                  }}
                >
                  +9k
                </div>
              </div>
              <div
                className="font-display"
                style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.2 }}
              >
                Joining 9,400+ creators
                <br />
                <span
                  style={{
                    fontWeight: 400,
                    color: "rgba(14,14,14,0.6)",
                    fontSize: 12,
                  }}
                >
                  who shipped their first flow this month
                </span>
              </div>
            </div>
          </div>
        </section>

        <section className="auth-form-panel">
          <div className="auth-form-inner">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                marginBottom: 4,
              }}
            >
              <h2
                className="font-display"
                style={{
                  margin: 0,
                  fontSize: 36,
                  fontWeight: 800,
                  letterSpacing: "-0.03em",
                }}
              >
                Create account
              </h2>
              <span
                className="font-mono"
                style={{
                  fontSize: 11,
                  textTransform: "uppercase",
                  letterSpacing: "0.18em",
                  color: "rgba(14,14,14,0.55)",
                }}
              >
                Step 1 of 3
              </span>
            </div>
            <p style={{ margin: 0, fontSize: 14, color: "rgba(14,14,14,0.7)" }}>
              Already have one?{" "}
              <Link
                to="/login"
                className="font-display"
                style={{ fontWeight: 700, textUnderlineOffset: 4 }}
              >
                Sign in →
              </Link>
            </p>

            <div className="steps-bar">
              <span className="is-done" />
              <span />
              <span />
            </div>

            {error && <div className="brut-error">{error}</div>}

            <div style={{ marginTop: 28 }}>
              <a
                href={`${API_URL}/auth/google/start`}
                className="btn-google press"
              >
                <GoogleG />
                Continue with Google
              </a>
            </div>

            <div className="divider-or">
              <span className="line" />
              <span className="label">or with email</span>
              <span className="line" />
            </div>

            <form onSubmit={handleSubmit}>
              <label className="brut-field-label" htmlFor="displayName">
                Display name
              </label>
              <input
                id="displayName"
                type="text"
                className="brut-input-2"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                autoComplete="name"
                maxLength={120}
                placeholder="Maya Rao"
              />

              <label
                className="brut-field-label"
                htmlFor="email"
                style={{ marginTop: 14 }}
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                className="brut-input-2"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                placeholder="you@brand.co"
                required
              />

              <label
                className="brut-field-label"
                htmlFor="password"
                style={{ marginTop: 14 }}
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                className="brut-input-2"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                required
                minLength={8}
                placeholder="At least 10 characters"
              />
              <StrengthMeter score={pwStrength.score} />
              <div
                className="font-mono"
                style={{
                  marginTop: 4,
                  fontSize: 11,
                  color: "rgba(14,14,14,0.55)",
                }}
              >
                {pwStrength.label} · 4-word passphrases work great.
              </div>

              <label
                style={{
                  marginTop: 16,
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 8,
                  fontSize: 13,
                  color: "rgba(14,14,14,0.8)",
                }}
              >
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  style={{
                    marginTop: 2,
                    width: 16,
                    height: 16,
                    accentColor: "var(--ink)",
                  }}
                  required
                />
                <span>
                  I agree to the{" "}
                  <a href="/terms" style={{ textUnderlineOffset: 4 }}>
                    Terms
                  </a>{" "}
                  and{" "}
                  <a href="/privacy" style={{ textUnderlineOffset: 4 }}>
                    Privacy Policy
                  </a>
                  .
                </span>
              </label>

              <button
                type="submit"
                className="btn-primary press"
                disabled={submitting || !agreed}
                style={{ marginTop: 20 }}
              >
                {submitting ? "Creating…" : "Create account →"}
              </button>

              <p className="fine-print" style={{ marginTop: 14 }}>
                No card required. 1,000 replies a month, free, forever.
              </p>
            </form>
          </div>
        </section>
      </main>
    </div>
  );
}

function passwordStrength(pw: string): { score: 0 | 1 | 2 | 3 | 4; label: string } {
  if (pw.length === 0) return { score: 0, label: "Weak" };
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw) && /\d/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  const clamped = Math.min(4, score) as 0 | 1 | 2 | 3 | 4;
  const labels: Record<0 | 1 | 2 | 3 | 4, string> = {
    0: "Weak",
    1: "Weak",
    2: "Okay",
    3: "Strong",
    4: "Strong",
  };
  return { score: clamped, label: labels[clamped] };
}

function StrengthMeter({ score }: { score: 0 | 1 | 2 | 3 | 4 }) {
  const bars = [0, 1, 2, 3];
  return (
    <div style={{ marginTop: 8, display: "flex", gap: 4 }}>
      {bars.map((i) => (
        <span
          key={i}
          style={{
            flex: 1,
            height: 6,
            border: "1px solid var(--ink)",
            background: i < score ? "var(--ink)" : "var(--paper)",
          }}
        />
      ))}
    </div>
  );
}

function GoogleG() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285f4"
        d="M21.6 12.2c0-.7-.1-1.4-.2-2H12v3.8h5.4c-.2 1.3-.9 2.4-2 3.1v2.6h3.3c1.9-1.8 3-4.4 3-7.5z"
      />
      <path
        fill="#34a853"
        d="M12 22c2.7 0 5-.9 6.7-2.4l-3.3-2.6c-.9.6-2 1-3.4 1-2.6 0-4.8-1.8-5.6-4.1H3v2.6C4.7 19.7 8.1 22 12 22z"
      />
      <path
        fill="#fbbc05"
        d="M6.4 13.9c-.2-.6-.3-1.3-.3-1.9s.1-1.3.3-1.9V7.5H3a10 10 0 0 0 0 9l3.4-2.6z"
      />
      <path
        fill="#ea4335"
        d="M12 6c1.5 0 2.8.5 3.8 1.5l2.9-2.9C17 2.9 14.7 2 12 2 8.1 2 4.7 4.3 3 7.5l3.4 2.6C7.2 7.8 9.4 6 12 6z"
      />
    </svg>
  );
}
